// WS-9 — Global admin step-up + idle handler.
//
// Intercepts sanitized 401/403 responses from admin API calls and
// surfaces a Shadcn dialog for TOTP step-up or re-authentication.
// Never persists admin tokens or session identifiers in
// localStorage / sessionStorage.

const STEP_UP_CODES = new Set([
  "admin_recent_auth_required",
  "admin_totp_step_up_required",
  "admin_sensitive_read_window_expired",
]);

const LIFECYCLE_CODES = new Set([
  "admin_session_expired",
  "admin_idle_timeout",
  "admin_session_revoked",
  "admin_session_required",
]);

let subscribers = new Set();

export function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function notify(evt) {
  subscribers.forEach((fn) => {
    try { fn(evt); } catch (_e) { /* isolate */ }
  });
}

/** Normalize a fetch response and extract the sanitized detail code. */
export async function inspectAdminResponse(resp) {
  if (!resp || resp.ok) return { ok: true };
  let detail = "";
  try {
    const j = await resp.clone().json();
    detail = (j && (j.detail || j.error || j.reason)) || "";
    if (typeof detail === "object" && detail !== null) {
      detail = detail.status || detail.reason || detail.code || "";
    }
  } catch (_e) {
    // no JSON body — leave detail empty
  }
  const kind = STEP_UP_CODES.has(detail)
    ? "step_up"
    : LIFECYCLE_CODES.has(detail)
      ? "lifecycle"
      : "other";
  if (kind === "step_up") notify({ kind, code: detail });
  if (kind === "lifecycle") notify({ kind, code: detail });
  return { ok: false, code: detail, kind, status: resp.status };
}

/** Convenience wrapper around fetch that raises step-up UI as needed. */
export async function adminFetch(url, init = {}) {
  const method = (init.method || "GET").toUpperCase();
  const headers = { ...(init.headers || {}) };
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    // Enforce the WS-8 CSRF barrier at every admin call site.
    headers["X-Admin-Request"] = "1";
  }
  const resp = await fetch(url, { credentials: "include", ...init, headers });
  await inspectAdminResponse(resp);
  return resp;
}

export const stepUpCodes = STEP_UP_CODES;
export const lifecycleCodes = LIFECYCLE_CODES;
